import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  FaEdit,
  FaFileInvoice,
  FaHistory,
  FaPrint,
  FaSearch,
  FaTimes,
} from "react-icons/fa";
import { HiOutlineDownload } from "react-icons/hi";
import SetupContainer from "../../../setupPage/SharedComponents/SetupContainer";
import Box from "../../../setupPage/SharedComponents/Box";
import PaymentSlip from "../../../bookings/components/Payment/PaymentSlip";
import AdminEditBookingModal from "../AdminBookings/AdminEditBookingModal";
import { contextProvider } from "../../../setupPage/SetupPagesContextApi/SetupPagesContextApi";
import { formatCurrency } from "../../../../utils/formatCurrency";
import {
  searchInvoiceCustomers,
  getParcelsByCustomer,
  getInvoicesByParcelId,
  getInvoiceForPrint,
} from "../../../../api/invoiceManagerApi";
import {
  fetchParcelCostDetails,
  fetchParcelDetailsByParcelNo,
  fetchParcelBookingByParcelNo,
  fetchParcelProductsForEdit,
} from "../../../../api/adminEditBookingApi";
import {
  buildInvoiceSlipFromPrintPayload,
  isRemeasureDueInvoice,
} from "../../../../utils/invoiceManagerSlip";
import {
  buildPaymentSlipPdfBlob,
  printPdfBlob,
} from "../../../payments/paymentSlipPdf";
import {
  INVOICE_ADJUSTMENT,
  LIVE_FALLBACK_NOTE,
} from "../../../../types/invoiceManager.types";

const THEME = {
  brown: "#1c1917",
  brownDark: "#292524",
  border: "#e7e5e4",
  orange: "#ea580c",
};

function getApiBase() {
  return process.env.REACT_APP_API_URL || "";
}

function getAuthHeaders() {
  const token =
    localStorage.getItem("sundorbonToken") || localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function fetchLookup(url) {
  try {
    const { data } = await axios.get(`${getApiBase()}${url}`, {
      headers: { accept: "*/*", ...getAuthHeaders() },
    });
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

const pickFirst = (...values) => {
  for (const v of values) {
    if (v != null && String(v).trim() !== "") return v;
  }
  return "";
};

const formatDateTime = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
};

const currencyOf = (...rows) => {
  for (const row of rows) {
    const v =
      row?.symbol ||
      row?.currencySymbol ||
      row?.currencyType ||
      row?.currencyCode;
    if (v) return v;
  }
  return "GBP";
};

const paymentIdOf = (row) => row?.id ?? row?.paymentId ?? null;

const invoiceKey = (row, idx = 0) =>
  `${row?.paymentInvoiceNo || "inv"}-${row?.version ?? paymentIdOf(row) ?? idx}`;

const pickDefaultInvoice = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const current = rows.find((r) => r.isCurrent || r.IsCurrent);
  if (current) return current;
  const nonSuperseded = rows.filter(
    (r) =>
      String(r.adjustmentType || "").toLowerCase() !== "superseded",
  );
  if (nonSuperseded.length) return nonSuperseded[nonSuperseded.length - 1];
  return rows[rows.length - 1];
};

const statusBadge = (statusId, statusName) => {
  const s = String(statusName || "").toLowerCase();
  if (statusId === 2 || s.includes("paid"))
    return "bg-emerald-100 text-emerald-800 border-emerald-200";
  if (statusId === 4 || s.includes("credit"))
    return "bg-sky-100 text-sky-800 border-sky-200";
  if (statusId === 3 || s.includes("decline"))
    return "bg-red-100 text-red-800 border-red-200";
  if (statusId === 1 || s.includes("pending"))
    return "bg-amber-100 text-amber-900 border-amber-200";
  return "bg-gray-100 text-gray-700 border-gray-200";
};

const adjustmentBadge = (type) => {
  const t = String(type || "None").toLowerCase();
  if (t === "superseded") return "bg-gray-200 text-gray-600 border-gray-300";
  if (t === "amended") return "bg-orange-100 text-orange-900 border-orange-200";
  if (t === "due") return "bg-amber-100 text-amber-900 border-amber-200";
  if (t === "credit") return "bg-sky-100 text-sky-800 border-sky-200";
  if (t === "none" || t === "original")
    return "bg-stone-100 text-stone-700 border-stone-200";
  return "bg-stone-100 text-stone-700 border-stone-200";
};

const adjustmentLabel = (type, isFirst) => {
  const t = String(type || "").trim();
  if (!t || t.toLowerCase() === "none") return isFirst ? "Original" : "None";
  return t;
};

const SummaryChip = ({ label, value, emphasize = false, muted = false }) => (
  <div
    className={`rounded-lg border px-3 py-2 min-w-[7.5rem] ${
      emphasize
        ? "bg-stone-900 border-stone-900 text-white"
        : "bg-white border-stone-200 text-stone-900"
    }`}
  >
    <p
      className={`text-[10px] font-semibold uppercase tracking-wide ${
        emphasize ? "text-white/70" : "text-stone-500"
      }`}
    >
      {label}
    </p>
    <p
      className={`text-sm font-bold mt-0.5 truncate ${
        muted && !emphasize ? "text-stone-500" : ""
      }`}
    >
      {value}
    </p>
  </div>
);

const MoneyCard = ({ label, value, dark = false, green = false }) => (
  <div
    className={`rounded-xl border px-4 py-3 ${
      dark
        ? "bg-stone-900 border-stone-900 text-white"
        : "bg-white border-stone-200"
    }`}
  >
    <p
      className={`text-[11px] font-semibold uppercase tracking-wide ${
        dark ? "text-white/70" : "text-stone-500"
      }`}
    >
      {label}
    </p>
    <p
      className={`text-base font-bold mt-1 ${
        green && !dark ? "text-emerald-700" : dark ? "text-white" : "text-stone-900"
      }`}
    >
      {value}
    </p>
  </div>
);

const AdminInvoiceManager = () => {
  const ctx = useContext(contextProvider) || {};
  const [extraLookups, setExtraLookups] = useState({
    dimensions: [],
    weights: [],
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [dimensions, weights] = await Promise.all([
        ctx.dimensions?.length
          ? Promise.resolve(ctx.dimensions)
          : fetchLookup("Dimension"),
        ctx.weight?.length
          ? Promise.resolve(ctx.weight)
          : fetchLookup("Weight"),
      ]);
      if (cancelled) return;
      setExtraLookups({ dimensions, weights });
    })();
    return () => {
      cancelled = true;
    };
  }, [ctx.dimensions, ctx.weight]);

  const lookups = useMemo(
    () => ({
      itemCategories: ctx.itemCategory || [],
      itemTypes: ctx.itemType || [],
      cargoTypes: ctx.cargoTypes || [],
      dimensions: extraLookups.dimensions?.length
        ? extraLookups.dimensions
        : ctx.dimensions || [],
      weights: extraLookups.weights?.length
        ? extraLookups.weights
        : ctx.weight || [],
    }),
    [
      ctx.itemCategory,
      ctx.itemType,
      ctx.cargoTypes,
      ctx.dimensions,
      ctx.weight,
      extraLookups,
    ],
  );

  const [query, setQuery] = useState("");
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerMenuOpen, setCustomerMenuOpen] = useState(false);

  const [parcels, setParcels] = useState([]);
  const [parcelsLoading, setParcelsLoading] = useState(false);
  const [selectedParcel, setSelectedParcel] = useState(null);

  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const [slipLoading, setSlipLoading] = useState(false);
  const [slipPaymentData, setSlipPaymentData] = useState(null);
  const [costSnapshot, setCostSnapshot] = useState({});
  const [editParcel, setEditParcel] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(true);
  const [showReceipt, setShowReceipt] = useState(false);
  const [printBusy, setPrintBusy] = useState(false);

  const searchRequestId = useRef(0);
  const slipRequestId = useRef(0);
  const printContentRef = useRef(null);

  const debouncedQuery = useMemo(() => query.trim(), [query]);

  const clearInvoicePreview = useCallback(() => {
    slipRequestId.current += 1;
    setSelectedInvoice(null);
    setSlipPaymentData(null);
    setCostSnapshot({});
    setSlipLoading(false);
  }, []);

  useEffect(() => {
    if (debouncedQuery.length < 2) {
      setCustomers([]);
      setCustomersLoading(false);
      return undefined;
    }

    // Selecting a customer fills the input with their name; don't re-search
    // or force the menu open again for that exact match.
    if (
      selectedCustomer &&
      debouncedQuery === String(selectedCustomer.senderName || "").trim()
    ) {
      setCustomersLoading(false);
      return undefined;
    }

    const requestId = ++searchRequestId.current;
    setCustomersLoading(true);
    const timer = setTimeout(async () => {
      try {
        const rows = await searchInvoiceCustomers(debouncedQuery);
        if (requestId !== searchRequestId.current) return;
        setCustomers(rows);
      } catch (err) {
        if (requestId !== searchRequestId.current) return;
        toast.error(
          err?.response?.data?.message ||
            err?.message ||
            "Customer search failed.",
        );
        setCustomers([]);
      } finally {
        if (requestId === searchRequestId.current) {
          setCustomersLoading(false);
        }
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [debouncedQuery, selectedCustomer]);

  const loadSlipForInvoice = useCallback(
    async (parcel, customer, invoiceRow, historyRows = null) => {
      if (!parcel?.parcelId || !invoiceRow) return null;

      const requestId = ++slipRequestId.current;
      setSelectedInvoice(invoiceRow);
      setSlipLoading(true);
      setSlipPaymentData(null);

      try {
        const parcelId = parcel.parcelId;
        const invoiceNo = invoiceRow?.paymentInvoiceNo;
        const [printPayload, costData, parcelDetails, historyParcel, liveProducts] =
          await Promise.all([
            invoiceNo
              ? getInvoiceForPrint(invoiceNo).catch((err) => {
                  console.error("GetInvoiceForPrint failed", err);
                  return null;
                })
              : Promise.resolve(null),
            fetchParcelCostDetails(parcelId).catch(() => ({})),
            fetchParcelDetailsByParcelNo(parcelId).catch(() => null),
            fetchParcelBookingByParcelNo(parcelId).catch(() => null),
            fetchParcelProductsForEdit(parcelId).catch(() => []),
          ]);

        if (requestId !== slipRequestId.current) return null;

        if (!printPayload) {
          toast.error(
            invoiceNo
              ? `Could not load print data for ${invoiceNo}.`
              : "Invoice number is missing.",
          );
          setSlipPaymentData(null);
          return null;
        }

        // Original (V1) ParcelWeight is the booking-time customer weight when
        // later remesaure overwrote CustomerParcelWeight.
        const history = historyRows || invoices || [];
        const originalInvoice =
          history.find((r) => Number(r.version) === 1) ||
          history.find(
            (r) =>
              !r.parentPaymentId &&
              String(r.adjustmentType || "").toLowerCase() !== "due",
          ) ||
          history[history.length - 1];
        let originalWeightOverlay = [];
        if (
          originalInvoice?.paymentInvoiceNo &&
          originalInvoice.paymentInvoiceNo !== invoiceNo
        ) {
          try {
            const originalPrint = await getInvoiceForPrint(
              originalInvoice.paymentInvoiceNo,
            );
            if (requestId !== slipRequestId.current) return null;
            originalWeightOverlay = (originalPrint?.lines || []).map((l) => ({
              customerParcelWeight: l.parcelWeight ?? l.ParcelWeight,
              CustomerParcelWeight: l.parcelWeight ?? l.ParcelWeight,
              customerParcelLength:
                l.customerParcelLength ??
                l.CustomerParcelLength ??
                l.parcelLength ??
                l.ParcelLength,
              customerParcelWidth:
                l.customerParcelWidth ??
                l.CustomerParcelWidth ??
                l.parcelWidth ??
                l.ParcelWidth,
              customerParcelHeight:
                l.customerParcelHeight ??
                l.CustomerParcelHeight ??
                l.parcelHeight ??
                l.ParcelHeight,
            }));
          } catch {
            originalWeightOverlay = [];
          }
        }

        // Prefer live CustomerParcel* ; fall back to V1 overlay when live
        // customer weight is missing or equals remesaure ParcelWeight.
        const mergedLiveProducts = (liveProducts || []).map((row, idx) => {
          const orig = originalWeightOverlay[idx] || originalWeightOverlay[0];
          if (!orig) return row;
          const liveCustomerW =
            row.customerParcelWeight ?? row.CustomerParcelWeight;
          const hubW = row.parcelWeight ?? row.ParcelWeight;
          const needsOrig =
            liveCustomerW == null ||
            liveCustomerW === "" ||
            (hubW != null && String(liveCustomerW) === String(hubW));
          if (!needsOrig) return row;
          return {
            ...row,
            customerParcelWeight:
              orig.customerParcelWeight ?? row.customerParcelWeight,
            CustomerParcelWeight:
              orig.CustomerParcelWeight ?? row.CustomerParcelWeight,
            customerParcelLength:
              row.customerParcelLength ??
              row.CustomerParcelLength ??
              orig.customerParcelLength,
            customerParcelWidth:
              row.customerParcelWidth ??
              row.CustomerParcelWidth ??
              orig.customerParcelWidth,
            customerParcelHeight:
              row.customerParcelHeight ??
              row.CustomerParcelHeight ??
              orig.customerParcelHeight,
          };
        });
        const liveForSlip =
          mergedLiveProducts.length > 0
            ? mergedLiveProducts
            : originalWeightOverlay;

        const detailObj =
          parcelDetails && typeof parcelDetails === "object"
            ? parcelDetails
            : {};
        const historyObj =
          historyParcel && typeof historyParcel === "object"
            ? historyParcel
            : {};

        const mergedCost = {
          ...costData,
          ...detailObj,
          senderAdditionalAddressInfo: pickFirst(
            detailObj.senderAdditionalAddressInfo,
            detailObj.SenderAdditionalAddressInfo,
            costData.senderAdditionalAddressInfo,
            costData.SenderAdditionalAddressInfo,
            historyObj.senderAdditionalAddressInfo,
            historyObj.senderAddress,
          ),
          senderDoorNo: pickFirst(
            printPayload.senderDoorNo,
            detailObj.senderDoorNo,
            detailObj.SenderDoorNo,
            costData.senderDoorNo,
            historyObj.senderDoorNo,
          ),
          senderStreetName: pickFirst(
            printPayload.senderStreetName,
            detailObj.senderStreetName,
            detailObj.SenderStreetName,
            costData.senderStreetName,
            historyObj.senderStreetName,
          ),
          senderCityId: pickFirst(
            detailObj.senderCityId,
            detailObj.SenderCityId,
            historyObj.senderCityId,
            historyObj.SenderCityId,
            costData.senderCityId,
          ),
          senderCountryId: pickFirst(
            detailObj.senderCountryId,
            detailObj.SenderCountryId,
            historyObj.senderCountryId,
            historyObj.SenderCountryId,
            costData.senderCountryId,
          ),
          senderPostCode: pickFirst(
            printPayload.senderPostCode,
            detailObj.senderPostCode,
            detailObj.SenderPostCode,
            historyObj.senderPostCode,
            costData.senderPostCode,
            parcel.senderPostCode,
          ),
          receiverName: pickFirst(
            printPayload.receiverName,
            detailObj.receiverName,
            detailObj.ReceiverName,
            historyObj.receiverName,
            costData.receiverName,
          ),
          receiverEmail: pickFirst(
            printPayload.receiverEmail,
            detailObj.receiverEmail,
            detailObj.ReceiverEmail,
            historyObj.receiverEmail,
            costData.receiverEmail,
          ),
          receiverPhone: pickFirst(
            printPayload.receiverPhone,
            detailObj.receiverPhone,
            detailObj.receiverMobileNo,
            detailObj.ReceiverMobileNo,
            historyObj.receiverPhone,
            costData.receiverPhone,
          ),
          receiverMobileNo: pickFirst(
            printPayload.receiverPhone,
            detailObj.receiverMobileNo,
            detailObj.receiverPhone,
            historyObj.receiverPhone,
          ),
          receiverPostCode: pickFirst(
            printPayload.receiverPostCode,
            detailObj.receiverPostCode,
            detailObj.ReceiverPostCode,
            historyObj.receiverPostCode,
            costData.receiverPostCode,
          ),
          receiverAdditionalAddressInfo: pickFirst(
            detailObj.receiverAdditionalAddressInfo,
            detailObj.ReceiverAdditionalAddressInfo,
            costData.receiverAdditionalAddressInfo,
            costData.ReceiverAdditionalAddressInfo,
            historyObj.receiverAdditionalAddressInfo,
            historyObj.receiverAddress,
          ),
          receiverDoorNo: pickFirst(
            printPayload.receiverDoorNo,
            detailObj.receiverDoorNo,
            detailObj.ReceiverDoorNo,
            costData.receiverDoorNo,
            historyObj.receiverDoorNo,
          ),
          receiverStreetName: pickFirst(
            printPayload.receiverStreetName,
            detailObj.receiverStreetName,
            detailObj.ReceiverStreetName,
            costData.receiverStreetName,
            historyObj.receiverStreetName,
          ),
          receiverAddress: pickFirst(
            detailObj.receiverAdditionalAddressInfo,
            detailObj.ReceiverAdditionalAddressInfo,
            detailObj.receiverAddress,
            historyObj.receiverAdditionalAddressInfo,
            historyObj.receiverAddress,
          ),
          receiverCityId: pickFirst(
            detailObj.receiverCityId,
            detailObj.ReceiverCityId,
            historyObj.receiverCityId,
            historyObj.ReceiverCityId,
            costData.receiverCityId,
          ),
          receiverCountryId: pickFirst(
            detailObj.receiverCountryId,
            detailObj.ReceiverCountryId,
            historyObj.receiverCountryId,
            historyObj.ReceiverCountryId,
            costData.receiverCountryId,
          ),
        };
        setCostSnapshot(mergedCost);

        const enrichedParcel = {
          ...historyObj,
          ...parcel,
          senderName: pickFirst(
            printPayload.senderName,
            parcel.senderName,
            customer?.senderName,
            historyObj.senderName,
            detailObj.senderName,
          ),
          senderEmail: pickFirst(
            printPayload.senderEmail,
            parcel.senderEmail,
            customer?.senderEmail,
            historyObj.senderEmail,
            detailObj.senderEmail,
          ),
          senderPhone: pickFirst(
            printPayload.senderPhone,
            parcel.senderPhone,
            customer?.senderPhone,
            historyObj.senderPhone,
            detailObj.senderPhone,
          ),
          senderPostCode: pickFirst(
            printPayload.senderPostCode,
            parcel.senderPostCode,
            historyObj.senderPostCode,
            detailObj.senderPostCode,
            detailObj.SenderPostCode,
          ),
          senderAddress: pickFirst(
            detailObj.senderAdditionalAddressInfo,
            detailObj.SenderAdditionalAddressInfo,
            historyObj.senderAdditionalAddressInfo,
            historyObj.senderAddress,
            detailObj.senderAddress,
          ),
          senderAdditionalAddressInfo: pickFirst(
            detailObj.senderAdditionalAddressInfo,
            detailObj.SenderAdditionalAddressInfo,
            historyObj.senderAdditionalAddressInfo,
          ),
          senderDoorNo: pickFirst(
            printPayload.senderDoorNo,
            detailObj.senderDoorNo,
            detailObj.SenderDoorNo,
            historyObj.senderDoorNo,
          ),
          senderStreetName: pickFirst(
            printPayload.senderStreetName,
            detailObj.senderStreetName,
            detailObj.SenderStreetName,
            historyObj.senderStreetName,
          ),
          senderCityId: pickFirst(
            detailObj.senderCityId,
            detailObj.SenderCityId,
            historyObj.senderCityId,
          ),
          senderCountryId: pickFirst(
            detailObj.senderCountryId,
            detailObj.SenderCountryId,
            historyObj.senderCountryId,
          ),
          receiverName: pickFirst(
            printPayload.receiverName,
            historyObj.receiverName,
            detailObj.receiverName,
            detailObj.ReceiverName,
            parcel.receiverName,
          ),
          receiverEmail: pickFirst(
            printPayload.receiverEmail,
            historyObj.receiverEmail,
            detailObj.receiverEmail,
            detailObj.ReceiverEmail,
            parcel.receiverEmail,
          ),
          receiverPhone: pickFirst(
            printPayload.receiverPhone,
            historyObj.receiverPhone,
            detailObj.receiverPhone,
            detailObj.receiverMobileNo,
            detailObj.ReceiverMobileNo,
            parcel.receiverPhone,
          ),
          receiverPostCode: pickFirst(
            printPayload.receiverPostCode,
            historyObj.receiverPostCode,
            detailObj.receiverPostCode,
            detailObj.ReceiverPostCode,
            parcel.receiverPostCode,
          ),
          receiverAddress: pickFirst(
            detailObj.receiverAdditionalAddressInfo,
            detailObj.ReceiverAdditionalAddressInfo,
            historyObj.receiverAdditionalAddressInfo,
            historyObj.receiverAddress,
            detailObj.receiverAddress,
            parcel.receiverAddress,
          ),
          receiverAdditionalAddressInfo: pickFirst(
            detailObj.receiverAdditionalAddressInfo,
            detailObj.ReceiverAdditionalAddressInfo,
            historyObj.receiverAdditionalAddressInfo,
          ),
          receiverDoorNo: pickFirst(
            printPayload.receiverDoorNo,
            detailObj.receiverDoorNo,
            detailObj.ReceiverDoorNo,
            historyObj.receiverDoorNo,
          ),
          receiverStreetName: pickFirst(
            printPayload.receiverStreetName,
            detailObj.receiverStreetName,
            detailObj.ReceiverStreetName,
            historyObj.receiverStreetName,
          ),
          receiverCityId: pickFirst(
            historyObj.receiverCityId,
            detailObj.receiverCityId,
            detailObj.ReceiverCityId,
            parcel.receiverCityId,
          ),
          receiverCountryId: pickFirst(
            historyObj.receiverCountryId,
            detailObj.receiverCountryId,
            detailObj.ReceiverCountryId,
            parcel.receiverCountryId,
          ),
          details: historyObj.details || parcel.details || [],
        };

        const slipData = buildInvoiceSlipFromPrintPayload({
          printPayload,
          parcelRow: enrichedParcel,
          costData: mergedCost,
          invoiceRow,
          invoiceHistory: historyRows || invoices,
          lookups,
          liveProducts: liveForSlip,
        });

        if (!slipData) {
          toast.error("Could not prepare invoice view from print data.");
          setSlipPaymentData(null);
          return null;
        }

        setSlipPaymentData(slipData);
        return slipData;
      } catch (err) {
        if (requestId !== slipRequestId.current) return null;
        toast.error(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to prepare invoice view.",
        );
        setSlipPaymentData(null);
        return null;
      } finally {
        if (requestId === slipRequestId.current) {
          setSlipLoading(false);
        }
      }
    },
    [lookups, invoices],
  );

  const loadParcels = useCallback(
    async (customer) => {
      if (!customer?.senderName) return;
      setParcelsLoading(true);
      setSelectedParcel(null);
      setInvoices([]);
      clearInvoicePreview();
      try {
        const rows = await getParcelsByCustomer({
          senderName: customer.senderName,
          senderEmail: customer.senderEmail,
        });
        setParcels(rows);
      } catch (err) {
        toast.error(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load parcels for customer.",
        );
        setParcels([]);
      } finally {
        setParcelsLoading(false);
      }
    },
    [clearInvoicePreview],
  );

  const loadInvoices = useCallback(
    async (parcel, customer) => {
      if (!parcel?.parcelId) return;
      setInvoicesLoading(true);
      clearInvoicePreview();
      setHistoryOpen(true);
      try {
        const rows = await getInvoicesByParcelId(parcel.parcelId);
        setInvoices(rows);
        const defaultInvoice = pickDefaultInvoice(rows);
        if (defaultInvoice) {
          await loadSlipForInvoice(parcel, customer, defaultInvoice, rows);
        }
      } catch (err) {
        toast.error(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load invoice versions.",
        );
        setInvoices([]);
      } finally {
        setInvoicesLoading(false);
      }
    },
    [clearInvoicePreview, loadSlipForInvoice],
  );

  // Re-resolve weight-band L×W×H once Dimension/Weight masters first become available.
  const dimMastersReady =
    (lookups.dimensions?.length || 0) > 0 ||
    (lookups.weights?.length || 0) > 0;
  const dimMastersWereReadyRef = useRef(false);
  useEffect(() => {
    const wasReady = dimMastersWereReadyRef.current;
    dimMastersWereReadyRef.current = dimMastersReady;
    if (!dimMastersReady || wasReady) return;
    if (!selectedInvoice || !selectedParcel) return;
    loadSlipForInvoice(
      selectedParcel,
      selectedCustomer,
      selectedInvoice,
      invoices,
    );
  }, [
    dimMastersReady,
    selectedInvoice,
    selectedParcel,
    selectedCustomer,
    invoices,
    loadSlipForInvoice,
  ]);

  const handleSelectCustomer = async (customer) => {
    // Cancel any in-flight search so a late response can't reopen the menu.
    searchRequestId.current += 1;
    setSelectedCustomer(customer);
    setQuery(customer.senderName || "");
    setCustomers([]);
    setCustomersLoading(false);
    setCustomerMenuOpen(false);
    await loadParcels(customer);
  };

  const handleSelectParcel = async (parcelId) => {
    const parcel = parcels.find((p) => String(p.parcelId) === String(parcelId));
    if (!parcel) return;
    setSelectedParcel(parcel);
    await loadInvoices(parcel, selectedCustomer);
  };

  const handleSelectInvoice = async (invoiceRow) => {
    if (!selectedParcel) return;
    await loadSlipForInvoice(
      selectedParcel,
      selectedCustomer,
      invoiceRow,
      invoices,
    );
  };

  const buildSlipPayload = async (invoiceRow) => {
    const parcelId = selectedParcel?.parcelId;
    const invoiceNo = invoiceRow?.paymentInvoiceNo;
    if (!parcelId || !invoiceRow || !invoiceNo) return null;

    const [printPayload, costData, parcelDetails, liveProducts] =
      await Promise.all([
        getInvoiceForPrint(invoiceNo),
        fetchParcelCostDetails(parcelId).catch(() => ({})),
        fetchParcelDetailsByParcelNo(parcelId).catch(() => null),
        fetchParcelProductsForEdit(parcelId).catch(() => []),
      ]);

    if (!printPayload) return null;

    const detailObj =
      parcelDetails && typeof parcelDetails === "object" ? parcelDetails : {};

    const enrichedParcel = {
      ...selectedParcel,
      senderName: pickFirst(
        printPayload.senderName,
        selectedParcel.senderName,
        selectedCustomer?.senderName,
        detailObj.senderName,
      ),
      senderEmail: pickFirst(
        printPayload.senderEmail,
        selectedParcel.senderEmail,
        selectedCustomer?.senderEmail,
        detailObj.senderEmail,
      ),
      senderPhone: pickFirst(
        printPayload.senderPhone,
        selectedParcel.senderPhone,
        selectedCustomer?.senderPhone,
        detailObj.senderPhone,
      ),
      senderPostCode: pickFirst(
        printPayload.senderPostCode,
        selectedParcel.senderPostCode,
        detailObj.senderPostCode,
        detailObj.SenderPostCode,
      ),
      senderAddress: pickFirst(
        detailObj.senderAdditionalAddressInfo,
        detailObj.SenderAdditionalAddressInfo,
        detailObj.senderAddress,
        selectedParcel.senderAddress,
      ),
      senderAdditionalAddressInfo: pickFirst(
        detailObj.senderAdditionalAddressInfo,
        detailObj.SenderAdditionalAddressInfo,
      ),
      senderDoorNo: pickFirst(
        printPayload.senderDoorNo,
        detailObj.senderDoorNo,
        detailObj.SenderDoorNo,
      ),
      senderStreetName: pickFirst(
        printPayload.senderStreetName,
        detailObj.senderStreetName,
        detailObj.SenderStreetName,
      ),
      senderCityId: pickFirst(detailObj.senderCityId, detailObj.SenderCityId),
      senderCountryId: pickFirst(
        detailObj.senderCountryId,
        detailObj.SenderCountryId,
      ),
      receiverName: pickFirst(
        printPayload.receiverName,
        detailObj.receiverName,
        detailObj.ReceiverName,
      ),
      receiverEmail: pickFirst(
        printPayload.receiverEmail,
        detailObj.receiverEmail,
        detailObj.ReceiverEmail,
      ),
      receiverPhone: pickFirst(
        printPayload.receiverPhone,
        detailObj.receiverPhone,
        detailObj.receiverMobileNo,
      ),
      receiverPostCode: pickFirst(
        printPayload.receiverPostCode,
        detailObj.receiverPostCode,
        detailObj.ReceiverPostCode,
      ),
      receiverAddress: pickFirst(
        detailObj.receiverAdditionalAddressInfo,
        detailObj.ReceiverAdditionalAddressInfo,
        detailObj.receiverAddress,
      ),
      receiverAdditionalAddressInfo: pickFirst(
        detailObj.receiverAdditionalAddressInfo,
        detailObj.ReceiverAdditionalAddressInfo,
      ),
      receiverDoorNo: pickFirst(
        printPayload.receiverDoorNo,
        detailObj.receiverDoorNo,
        detailObj.ReceiverDoorNo,
      ),
      receiverStreetName: pickFirst(
        printPayload.receiverStreetName,
        detailObj.receiverStreetName,
        detailObj.ReceiverStreetName,
      ),
      receiverCityId: pickFirst(
        detailObj.receiverCityId,
        detailObj.ReceiverCityId,
      ),
      receiverCountryId: pickFirst(
        detailObj.receiverCountryId,
        detailObj.ReceiverCountryId,
      ),
    };

    return buildInvoiceSlipFromPrintPayload({
      printPayload,
      parcelRow: enrichedParcel,
      costData: {
        ...costData,
        ...detailObj,
        senderAdditionalAddressInfo: pickFirst(
          detailObj.senderAdditionalAddressInfo,
          detailObj.SenderAdditionalAddressInfo,
          costData.senderAdditionalAddressInfo,
        ),
        senderCityId: pickFirst(
          detailObj.senderCityId,
          detailObj.SenderCityId,
          costData.senderCityId,
        ),
        senderCountryId: pickFirst(
          detailObj.senderCountryId,
          detailObj.SenderCountryId,
          costData.senderCountryId,
        ),
        receiverAdditionalAddressInfo: pickFirst(
          detailObj.receiverAdditionalAddressInfo,
          detailObj.ReceiverAdditionalAddressInfo,
          detailObj.receiverAddress,
          costData.receiverAdditionalAddressInfo,
        ),
        receiverCityId: pickFirst(
          detailObj.receiverCityId,
          detailObj.ReceiverCityId,
          costData.receiverCityId,
        ),
        receiverCountryId: pickFirst(
          detailObj.receiverCountryId,
          detailObj.ReceiverCountryId,
          costData.receiverCountryId,
        ),
      },
      invoiceRow,
      invoiceHistory: invoices,
      lookups,
      liveProducts,
    });
  };

  const downloadViaNewTab = async (invoiceRow) => {
    try {
      // Always prefer the slip already loaded for the selected version
      const slipData =
        slipPaymentData &&
        selectedInvoice &&
        invoiceKey(selectedInvoice) === invoiceKey(invoiceRow)
          ? slipPaymentData
          : await buildSlipPayload(invoiceRow);
      if (!slipData) {
        toast.error("Could not prepare this invoice version for download.");
        return;
      }
      localStorage.setItem("paymentSlipData", JSON.stringify(slipData));
      window.open("/paymentslip", "_blank");
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to open invoice page.",
      );
    }
  };

  const waitForPrintReceiptElement = () =>
    new Promise((resolve, reject) => {
      const maxAttempts = 40;
      const delayMs = 100;
      let attempts = 0;

      const tryFind = () => {
        const root = printContentRef.current;
        const element =
          root?.querySelector?.(".receipt-container") || root || null;
        if (element) {
          resolve(element);
          return;
        }
        attempts += 1;
        if (attempts >= maxAttempts) {
          reject(new Error("Invoice receipt is not ready to print."));
          return;
        }
        setTimeout(tryFind, delayMs);
      };

      tryFind();
    });

  const printSelected = async () => {
    if (!selectedInvoice || !selectedParcel || printBusy) return;

    setPrintBusy(true);
    try {
      let slipData = slipPaymentData;
      if (!slipData) {
        slipData = await loadSlipForInvoice(
          selectedParcel,
          selectedCustomer,
          selectedInvoice,
          invoices,
        );
      }
      if (!slipData) {
        toast.error("Could not prepare this invoice for printing.");
        return;
      }

      const element = await waitForPrintReceiptElement();

      const { blob } = await buildPaymentSlipPdfBlob(element, {
        bookingBarcodeNumber:
          slipData.bookingBarcodeNumber || selectedParcel?.parcelId,
        recordSerialNo:
          slipData.recordSerialNo || selectedInvoice?.paymentInvoiceNo,
      });

      printPdfBlob(blob);
    } catch (err) {
      toast.error(
        err?.message || "Failed to prepare invoice PDF for printing.",
      );
    } finally {
      setPrintBusy(false);
    }
  };

  const openEditBooking = () => {
    if (!selectedParcel?.parcelId) return;
    setEditParcel({
      parcelNo: selectedParcel.parcelId,
      senderName:
        selectedParcel.senderName || selectedCustomer?.senderName || "",
      senderEmail:
        selectedParcel.senderEmail || selectedCustomer?.senderEmail || "",
      senderPhone:
        selectedParcel.senderPhone || selectedCustomer?.senderPhone || "",
      senderPostCode: selectedParcel.senderPostCode || "",
      receiverName: slipPaymentData?.receiverName || "",
      receiverEmail: slipPaymentData?.receiverEmail || "",
      receiverPhone: slipPaymentData?.receiverMobileNumber || "",
      receiverPostCode: slipPaymentData?.receiverPostCode || "",
      details: [],
    });
  };

  const handleEditSaved = async () => {
    setEditParcel(null);
    if (!selectedParcel) return;
    await loadInvoices(selectedParcel, selectedCustomer);
  };

  const parentVersionLabel = (invoiceRow) => {
    const parentId = invoiceRow?.parentPaymentId;
    if (parentId == null) return null;
    const parent = invoices.find(
      (r) => Number(paymentIdOf(r)) === Number(parentId),
    );
    if (parent?.paymentInvoiceNo) {
      return `amended from ${parent.paymentInvoiceNo}`;
    }
    return `amended from #${parentId}`;
  };

  const currency = currencyOf(
    selectedInvoice,
    slipPaymentData,
    costSnapshot,
    selectedParcel,
  );

  const versionMoney = slipPaymentData?.versionMoney || null;
  const paymentBreakdown = slipPaymentData?.paymentBreakdown || null;

  const invoiceAmount = Number(
    slipPaymentData?.orderAmount ??
      versionMoney?.total ??
      selectedInvoice?.orderAmount ??
      selectedParcel?.orderPayableAmount ??
      0,
  );

  const isPaid =
    Number(selectedInvoice?.paymentStatusId) === 2 ||
    String(selectedInvoice?.statusName || "")
      .toLowerCase()
      .includes("paid");

  const paidAmount = Number(
    versionMoney?.paid ??
      paymentBreakdown?.paid ??
      (isPaid ? invoiceAmount : 0),
  );
  const dueAmount = Number(
    versionMoney?.due ?? paymentBreakdown?.due ?? (isPaid ? 0 : invoiceAmount),
  );

  const discountDisplay = Number(
    versionMoney?.discount ??
      (isRemeasureDueInvoice(selectedInvoice)
        ? costSnapshot?.discountAmount
        : 0) ??
      0,
  );
  const vatDisplay = Number(
    versionMoney?.vat ??
      (isRemeasureDueInvoice(selectedInvoice)
        ? costSnapshot?.vaT_TaxAmount
        : 0) ??
      0,
  );
  const pickupDisplay = Number(
    versionMoney?.pickup ??
      (isRemeasureDueInvoice(selectedInvoice)
        ? costSnapshot?.pickupCost || costSnapshot?.pickUpCost
        : 0) ??
      0,
  );

  const lineItems = slipPaymentData?.producDetails || [];
  const firstInvoice = invoices[0];
  const selectedIsFirst =
    selectedInvoice &&
    firstInvoice &&
    invoiceKey(selectedInvoice) === invoiceKey(firstInvoice);

  return (
    <SetupContainer>
      <Box top>
      <div className="space-y-4 pb-8">
        {/* Page header */}
        <div className="flex items-start gap-3">
          <div
            className="h-11 w-11 rounded-lg flex items-center justify-center text-white font-black text-xl shadow-sm"
            style={{ backgroundColor: THEME.brown }}
          >
            I
          </div>
          <div>
            <h1 className="text-2xl font-bold text-stone-900 leading-tight">
              Invoice Manager
            </h1>
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-stone-500 mt-0.5">
              Admin · Parcel invoice snapshots
            </p>
          </div>
        </div>

        {/* Select order */}
        <section className="rounded-2xl border border-stone-200 bg-white p-4 shadow-sm">
          <p className="text-[11px] font-bold uppercase tracking-wide text-stone-500 mb-3">
            Select order
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="relative">
              <label className="block text-[11px] font-bold uppercase tracking-wide text-stone-500 mb-1">
                Customer
              </label>
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => {
                    const next = e.target.value;
                    setQuery(next);
                    setCustomerMenuOpen(true);
                    if (
                      selectedCustomer &&
                      next.trim() !==
                        String(selectedCustomer.senderName || "").trim()
                    ) {
                      setSelectedCustomer(null);
                    }
                  }}
                  onFocus={() => {
                    if (
                      customers.length &&
                      !(
                        selectedCustomer &&
                        debouncedQuery ===
                          String(selectedCustomer.senderName || "").trim()
                      )
                    ) {
                      setCustomerMenuOpen(true);
                    }
                  }}
                  placeholder="Search sender name (≥ 2 chars)"
                  className="w-full h-11 pl-9 pr-3 rounded-lg border-2 outline-none bg-white text-sm font-medium"
                  style={{ borderColor: THEME.border }}
                />
              </div>
              {customerMenuOpen && debouncedQuery.length >= 2 ? (
                <ul className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto rounded-lg border border-stone-200 bg-white shadow-lg">
                  {customersLoading ? (
                    <li className="px-3 py-3 text-sm text-stone-500">
                      Searching…
                    </li>
                  ) : customers.length === 0 ? (
                    <li className="px-3 py-3 text-sm text-stone-500">
                      No customers found.
                    </li>
                  ) : (
                    customers.map((c, idx) => (
                      <li key={`${c.senderName}-${c.senderEmail || idx}`}>
                        <button
                          type="button"
                          className="w-full text-left px-3 py-2.5 hover:bg-amber-50 border-b border-stone-100 last:border-0"
                          onClick={() => handleSelectCustomer(c)}
                        >
                          <p className="font-semibold text-stone-900 text-sm">
                            {c.senderName}
                          </p>
                          <p className="text-xs text-stone-500 truncate">
                            {c.senderEmail || "No email"}
                            {c.parcelCount != null
                              ? ` · ${c.parcelCount} parcels`
                              : ""}
                          </p>
                        </button>
                      </li>
                    ))
                  )}
                </ul>
              ) : null}
            </div>

            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wide text-stone-500 mb-1">
                Parcel / order
              </label>
              <select
                value={selectedParcel?.parcelId || ""}
                onChange={(e) => handleSelectParcel(e.target.value)}
                disabled={!selectedCustomer || parcelsLoading}
                className="w-full h-11 px-3 rounded-lg border-2 outline-none bg-white text-sm font-medium disabled:bg-stone-50 disabled:text-stone-400"
                style={{ borderColor: THEME.border }}
              >
                <option value="">
                  {parcelsLoading
                    ? "Loading parcels…"
                    : selectedCustomer
                      ? "Select a parcel"
                      : "Select a customer first"}
                </option>
                {parcels.map((p) => (
                  <option key={p.parcelId} value={p.parcelId}>
                    #{p.parcelId}
                    {p.recordSerialNo ? ` · ${p.recordSerialNo}` : ""}
                    {` · ${formatCurrency(p.orderPayableAmount, currencyOf(p))}`}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {selectedParcel ? (
            <div className="mt-4 flex flex-wrap gap-2">
              <SummaryChip
                label="Tracking"
                value={
                  selectedParcel.recordSerialNo ||
                  selectedParcel.barcode ||
                  `#${selectedParcel.parcelId}`
                }
              />
              <SummaryChip
                label="Total"
                value={formatCurrency(
                  selectedParcel.orderPayableAmount,
                  currencyOf(selectedParcel),
                )}
              />
              <SummaryChip
                label="Payment"
                value={selectedInvoice?.statusName || "—"}
              />
              <SummaryChip
                label="Paid"
                value={formatCurrency(paidAmount, currency)}
                muted={!isPaid}
              />
              <SummaryChip
                label="Due"
                value={formatCurrency(dueAmount, currency)}
                emphasize={dueAmount > 0}
              />
              <SummaryChip
                label="Versions"
                value={String(invoices.length || 0)}
              />
            </div>
          ) : null}
        </section>

        {!selectedParcel ? (
          <section className="rounded-2xl border border-dashed border-stone-300 bg-stone-50 px-6 py-16 text-center">
            <FaFileInvoice className="mx-auto text-3xl text-stone-400 mb-3" />
            <p className="text-stone-600 font-medium">
              Search a customer and select a parcel to open the invoice snapshot.
            </p>
          </section>
        ) : (
          <div
            className={`grid grid-cols-1 gap-4 items-start ${
              historyOpen ? "xl:grid-cols-[1fr_320px]" : ""
            }`}
          >
            {/* Main invoice snapshot */}
            <section className="rounded-2xl border border-stone-200 bg-white shadow-sm overflow-hidden">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3 px-4 py-4 border-b border-stone-100">
                <div>
                  <p className="font-mono text-lg font-bold text-stone-900 break-all">
                    {selectedInvoice?.paymentInvoiceNo ||
                      (slipLoading ? "Loading…" : "No invoice")}
                  </p>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {selectedInvoice ? (
                      <>
                        <span className="inline-flex px-2 py-0.5 rounded-md text-[11px] font-bold bg-stone-900 text-white">
                          V{selectedInvoice.version ?? "—"}
                        </span>
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-md text-[11px] font-semibold border ${adjustmentBadge(
                            selectedInvoice.adjustmentType,
                          )}`}
                        >
                          {adjustmentLabel(
                            selectedInvoice.adjustmentType,
                            selectedIsFirst,
                          )}
                        </span>
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-md text-[11px] font-semibold border ${statusBadge(
                            selectedInvoice.paymentStatusId,
                            selectedInvoice.statusName,
                          )}`}
                        >
                          {selectedInvoice.statusName || "—"}
                        </span>
                        {selectedInvoice.isCurrent ? (
                          <span className="inline-flex px-2 py-0.5 rounded-md text-[11px] font-bold bg-emerald-600 text-white">
                            Current
                          </span>
                        ) : null}
                      </>
                    ) : null}
                  </div>
                  {parentVersionLabel(selectedInvoice) ? (
                    <p className="text-xs text-orange-800 mt-2 font-medium">
                      {parentVersionLabel(selectedInvoice)}
                    </p>
                  ) : null}
                </div>

                <div className="flex flex-wrap gap-2 no-print">
                  <button
                    type="button"
                    onClick={printSelected}
                    disabled={!selectedInvoice || slipLoading || printBusy}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-stone-300 bg-white text-sm font-semibold hover:bg-stone-50 disabled:opacity-50"
                  >
                    <FaPrint />
                    {printBusy ? "Preparing…" : "Print"}
                  </button>
                  <button
                    type="button"
                    onClick={() => downloadViaNewTab(selectedInvoice)}
                    disabled={!selectedInvoice || slipLoading}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-stone-300 bg-white text-sm font-semibold hover:bg-stone-50 disabled:opacity-50"
                  >
                    <HiOutlineDownload className="text-base" />
                    Download
                  </button>
                  <button
                    type="button"
                    onClick={() => setHistoryOpen((v) => !v)}
                    className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-semibold ${
                      historyOpen
                        ? "border-stone-900 bg-stone-900 text-white"
                        : "border-stone-300 bg-white hover:bg-stone-50"
                    }`}
                  >
                    <FaHistory />
                    History
                    {invoices.length ? ` (${invoices.length})` : ""}
                  </button>
                  <button
                    type="button"
                    onClick={openEditBooking}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-white"
                    style={{ backgroundColor: THEME.orange }}
                  >
                    <FaEdit />
                    Amend
                  </button>
                </div>
              </div>

              {slipLoading ? (
                <div className="px-4 py-16 text-center text-stone-500 font-medium">
                  Loading invoice details…
                </div>
              ) : !selectedInvoice ? (
                <div className="px-4 py-16 text-center text-stone-500 font-medium">
                  {invoicesLoading
                    ? "Loading invoice versions…"
                    : "No invoices for this parcel."}
                </div>
              ) : (
                <div className="p-4 space-y-5">
                  {slipPaymentData?.usedLiveFallback ? (
                    <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-900">
                      {LIVE_FALLBACK_NOTE}
                    </p>
                  ) : null}

                  {slipPaymentData?.amendmentDetails ||
                  slipPaymentData?.remeasurement ? (
                    <div className="rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-3 text-sm">
                      <p className="text-[11px] font-bold uppercase tracking-wide text-amber-800 mb-2">
                        Amendment &amp; dimensions
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="space-y-1 text-stone-800">
                          {slipPaymentData.remeasurement?.customerInput ? (
                            <p>
                              <span className="font-semibold text-stone-700">
                                Customer given:
                              </span>{" "}
                              {(
                                [
                                  slipPaymentData.remeasurement.customerInput
                                    .length,
                                  slipPaymentData.remeasurement.customerInput
                                    .width,
                                  slipPaymentData.remeasurement.customerInput
                                    .height,
                                ].filter((v) => v != null && v !== "").length ===
                                3
                                  ? `(${slipPaymentData.remeasurement.customerInput.length}×${slipPaymentData.remeasurement.customerInput.width}×${slipPaymentData.remeasurement.customerInput.height} in)`
                                  : "—"
                              )}
                              {slipPaymentData.remeasurement.customerInput
                                .weight
                                ? ` · ${slipPaymentData.remeasurement.customerInput.weight}kg`
                                : ""}
                            </p>
                          ) : null}
                          {slipPaymentData.remeasurement?.hubMeasurement ? (
                            <p>
                              <span className="font-semibold text-orange-700">
                                Weight band:
                              </span>{" "}
                              {(
                                [
                                  slipPaymentData.remeasurement.hubMeasurement
                                    .length,
                                  slipPaymentData.remeasurement.hubMeasurement
                                    .width,
                                  slipPaymentData.remeasurement.hubMeasurement
                                    .height,
                                ].filter((v) => v != null && v !== "").length ===
                                3
                                  ? `(${slipPaymentData.remeasurement.hubMeasurement.length}×${slipPaymentData.remeasurement.hubMeasurement.width}×${slipPaymentData.remeasurement.hubMeasurement.height} in)`
                                  : "—"
                              )}
                              {slipPaymentData.remeasurement.hubMeasurement
                                .weight
                                ? ` · ${slipPaymentData.remeasurement.hubMeasurement.weight}kg`
                                : ""}
                            </p>
                          ) : null}
                        </div>
                        <div className="space-y-1 text-stone-800">
                          {slipPaymentData.amendmentDetails?.adjustmentType ? (
                            <p>
                              <span className="font-semibold">Type:</span>{" "}
                              {slipPaymentData.amendmentDetails.adjustmentType}
                              {slipPaymentData.amendmentDetails.version != null
                                ? ` · V${slipPaymentData.amendmentDetails.version}`
                                : ""}
                            </p>
                          ) : null}
                          {slipPaymentData.amendmentDetails?.parentInvoiceNo ||
                          parentVersionLabel(selectedInvoice) ? (
                            <p>
                              <span className="font-semibold">
                                Amended from:
                              </span>{" "}
                              {slipPaymentData.amendmentDetails
                                ?.parentInvoiceNo ||
                                parentVersionLabel(selectedInvoice)}
                            </p>
                          ) : null}
                          {slipPaymentData.amendmentDetails?.description ||
                          slipPaymentData.remeasurement?.note ? (
                            <p>
                              <span className="font-semibold">Note:</span>{" "}
                              {slipPaymentData.amendmentDetails?.description ||
                                slipPaymentData.remeasurement?.note}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ) : null}

                  {/* Money grid — scoped to selected invoice version */}
                  <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-2">
                    <MoneyCard
                      label="Invoice amount"
                      value={formatCurrency(invoiceAmount, currency)}
                      dark
                    />
                    <MoneyCard
                      label="Discount"
                      value={formatCurrency(discountDisplay, currency)}
                    />
                    <MoneyCard
                      label="VAT / Tax"
                      value={formatCurrency(vatDisplay, currency)}
                    />
                    <MoneyCard
                      label="Pickup"
                      value={formatCurrency(pickupDisplay, currency)}
                    />
                    <MoneyCard
                      label="Paid"
                      value={formatCurrency(paidAmount, currency)}
                      green={paidAmount > 0 && dueAmount <= 0}
                    />
                    <MoneyCard
                      label="Balance due"
                      value={formatCurrency(dueAmount, currency)}
                    />
                    <MoneyCard
                      label="Invoice date"
                      value={formatDateTime(selectedInvoice.creationDate)}
                    />
                  </div>

                  {/* Parties */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="rounded-xl border border-stone-200 p-3">
                      <p className="text-[11px] font-bold uppercase tracking-wide text-orange-600 mb-2">
                        Sender
                      </p>
                      <p className="font-semibold text-stone-900">
                        {slipPaymentData?.senderName || "—"}
                      </p>
                      <p className="text-sm text-stone-600">
                        {slipPaymentData?.senderEmail || "—"}
                      </p>
                      <p className="text-sm text-stone-600">
                        {slipPaymentData?.senderMobileNumber || "—"}
                      </p>
                    </div>
                    <div className="rounded-xl border border-stone-200 p-3">
                      <p className="text-[11px] font-bold uppercase tracking-wide text-orange-600 mb-2">
                        Receiver
                      </p>
                      <p className="font-semibold text-stone-900">
                        {slipPaymentData?.receiverName || "—"}
                      </p>
                      <p className="text-sm text-stone-600">
                        {slipPaymentData?.receiverEmail || "—"}
                      </p>
                      <p className="text-sm text-stone-600">
                        {slipPaymentData?.receiverMobileNumber || "—"}
                      </p>
                    </div>
                  </div>

                  {/* Line items */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-[11px] font-bold uppercase tracking-wide text-stone-500">
                        Line items
                      </p>
                      <button
                        type="button"
                        onClick={() => setShowReceipt((v) => !v)}
                        className="text-xs font-semibold text-stone-700 hover:underline no-print"
                      >
                        {showReceipt ? "Hide receipt layout" : "Show receipt layout"}
                      </button>
                    </div>
                    <div className="overflow-x-auto rounded-xl border border-stone-200">
                      <table className="min-w-full text-sm">
                        <thead className="bg-stone-50 text-left text-[11px] uppercase tracking-wide text-stone-500">
                          <tr>
                            <th className="px-3 py-2.5 font-semibold">#</th>
                            <th className="px-3 py-2.5 font-semibold">
                              Description
                            </th>
                            <th className="px-3 py-2.5 font-semibold">Type</th>
                            <th className="px-3 py-2.5 font-semibold">Qty</th>
                            <th className="px-3 py-2.5 font-semibold">
                              Dimensions
                            </th>
                            <th className="px-3 py-2.5 font-semibold text-right">
                              Value
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {lineItems.length === 0 ? (
                            <tr>
                              <td
                                colSpan={6}
                                className="px-3 py-8 text-center text-stone-500"
                              >
                                No product lines on this booking.
                              </td>
                            </tr>
                          ) : (
                            lineItems.map((item, idx) => (
                              <tr
                                key={idx}
                                className="border-t border-stone-100"
                              >
                                <td className="px-3 py-2.5 font-semibold text-orange-600">
                                  {idx + 1}
                                </td>
                                <td className="px-3 py-2.5">
                                  <p className="font-medium text-stone-900">
                                    {item.itemDescription || "—"}
                                  </p>
                                  <p className="text-xs text-stone-500">
                                    {item.productGroupName || "—"}
                                  </p>
                                </td>
                                <td className="px-3 py-2.5 text-stone-700">
                                  {item.productTypeName || "—"}
                                </td>
                                <td className="px-3 py-2.5">
                                  {item.productQty || 1}
                                </td>
                                <td className="px-3 py-2.5 text-xs text-stone-600">
                                  <div className="flex flex-col gap-0.5">
                                    {item.customerLength &&
                                    item.customerWidth &&
                                    item.customerHeight ? (
                                      <span>
                                        <span className="font-semibold text-stone-700">
                                          Customer:
                                        </span>{" "}
                                        {item.customerLength}×
                                        {item.customerWidth}×
                                        {item.customerHeight}
                                        {item.customerWeight
                                          ? ` · ${item.customerWeight}kg`
                                          : ""}
                                      </span>
                                    ) : null}
                                    {(item.weightBandLength ||
                                      item.itemLength) &&
                                    (item.weightBandWidth || item.itemWidth) &&
                                    (item.weightBandHeight ||
                                      item.itemHeight) ? (
                                      <span>
                                        <span className="font-semibold text-orange-700">
                                          Weight band:
                                        </span>{" "}
                                        {item.weightDescription || item.preset
                                          ? `${item.weightDescription || item.preset} · `
                                          : ""}
                                        {item.weightBandLength ||
                                          item.itemLength}
                                        ×
                                        {item.weightBandWidth || item.itemWidth}
                                        ×
                                        {item.weightBandHeight ||
                                          item.itemHeight}
                                        {item.itemWeight
                                          ? ` · ${item.itemWeight}kg`
                                          : item.minimumWeight != null &&
                                              item.maximumWeight != null
                                            ? ` · ${item.minimumWeight}-${item.maximumWeight}kg`
                                            : ""}
                                      </span>
                                    ) : !item.customerLength &&
                                      !item.customerWidth ? (
                                      <span>—</span>
                                    ) : null}
                                  </div>
                                </td>
                                <td className="px-3 py-2.5 text-right font-medium">
                                  {formatCurrency(item.productValue, currency)}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Receipt layout — always mounted for PDF print capture */}
                  {slipPaymentData ? (
                    <div
                      ref={printContentRef}
                      className={
                        showReceipt
                          ? "rounded-xl border border-stone-200 bg-stone-50 p-2"
                          : "fixed left-[-9999px] top-0 z-[-1] w-[210mm] bg-white"
                      }
                      aria-hidden={!showReceipt}
                    >
                      {slipPaymentData.usedLiveFallback ? (
                        <p className="mb-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-900">
                          {LIVE_FALLBACK_NOTE}
                        </p>
                      ) : null}
                      <PaymentSlip
                        paymentDataOverride={slipPaymentData}
                        disableAutoUpload
                        embedded
                      />
                    </div>
                  ) : null}
                </div>
              )}
            </section>

            {/* History panel */}
            {historyOpen ? (
              <aside className="rounded-2xl border border-stone-200 bg-white shadow-sm overflow-hidden xl:sticky xl:top-4 no-print">
                <div
                  className="flex items-center justify-between px-4 py-3 text-white"
                  style={{ backgroundColor: THEME.brown }}
                >
                  <div>
                    <h2 className="font-bold flex items-center gap-2">
                      <FaHistory />
                      Invoice history
                    </h2>
                    <p className="text-xs text-white/75 mt-0.5">
                      Oldest → newest · click to open
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setHistoryOpen(false)}
                    className="p-1.5 rounded-md hover:bg-white/10"
                    aria-label="Close history"
                  >
                    <FaTimes />
                  </button>
                </div>

                <div className="max-h-[70vh] overflow-y-auto">
                  {invoicesLoading ? (
                    <p className="px-4 py-8 text-sm text-stone-500 text-center">
                      Loading history…
                    </p>
                  ) : invoices.length === 0 ? (
                    <p className="px-4 py-8 text-sm text-stone-500 text-center">
                      No versions yet.
                    </p>
                  ) : (
                    <ul className="divide-y divide-stone-100">
                      {[...invoices].reverse().map((inv, revIdx) => {
                        const idx = invoices.length - 1 - revIdx;
                        const selected =
                          selectedInvoice &&
                          invoiceKey(selectedInvoice, idx) ===
                            invoiceKey(inv, idx);
                        const superseded =
                          String(inv.adjustmentType || "").toLowerCase() ===
                          "superseded";
                        return (
                          <li key={invoiceKey(inv, idx)}>
                            <button
                              type="button"
                              onClick={() => handleSelectInvoice(inv)}
                              className={`w-full text-left px-4 py-3 transition-colors ${
                                selected
                                  ? "bg-amber-50"
                                  : "hover:bg-stone-50"
                              } ${superseded ? "opacity-60" : ""}`}
                            >
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-xs font-black uppercase tracking-wide text-stone-900">
                                  V{inv.version ?? idx + 1}
                                  {inv.isCurrent ? (
                                    <span className="ml-1 text-emerald-700">
                                      · current
                                    </span>
                                  ) : null}
                                </span>
                                <span
                                  className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold border ${statusBadge(
                                    inv.paymentStatusId,
                                    inv.statusName,
                                  )}`}
                                >
                                  {inv.statusName || "—"}
                                </span>
                              </div>
                              <p className="font-mono text-xs font-semibold text-stone-800 mt-1 break-all">
                                {inv.paymentInvoiceNo || "—"}
                              </p>
                              <div className="flex items-center justify-between gap-2 mt-1">
                                <span className="text-sm font-bold text-stone-900">
                                  {formatCurrency(
                                    inv.orderAmount,
                                    currencyOf(inv, selectedParcel),
                                  )}
                                </span>
                                <span
                                  className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold border ${adjustmentBadge(
                                    inv.adjustmentType,
                                  )}`}
                                >
                                  {adjustmentLabel(
                                    inv.adjustmentType,
                                    idx === 0,
                                  )}
                                </span>
                              </div>
                              <p className="text-[11px] text-stone-500 mt-1">
                                {formatDateTime(inv.creationDate)}
                              </p>
                              {parentVersionLabel(inv) ? (
                                <p className="text-[11px] text-orange-800 mt-1">
                                  {parentVersionLabel(inv)}
                                </p>
                              ) : null}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </aside>
            ) : null}
          </div>
        )}
      </div>
      </Box>

      <AdminEditBookingModal
        open={!!editParcel}
        parcel={editParcel}
        onClose={() => setEditParcel(null)}
        onSaved={handleEditSaved}
      />
    </SetupContainer>
  );
};

export default AdminInvoiceManager;
